import * as React from "react";

const TABLET_BREAKPOINT_MIN = 768;
const TABLET_BREAKPOINT_MAX = 1668;

// iPad Pro 11 inch M4 specific dimensions
const IPAD_PRO_11_M4_WIDTH = 1668;

export function useIsTablet() {
  const [isTablet, setIsTablet] = React.useState<boolean | undefined>(undefined);
  const [isHydrated, setIsHydrated] = React.useState(false);

  React.useEffect(() => {
    setIsHydrated(true);

    const checkTablet = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const minDimension = Math.min(width, height);
      const maxDimension = Math.max(width, height);

      // Check for touch capability
      const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

      // Check for iPad specifically (iPads report as Mac in user agent)
      const isIPad =
        /iPad/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && isTouchDevice && navigator.maxTouchPoints > 1);

      const isInTabletRange =
        minDimension >= TABLET_BREAKPOINT_MIN &&
        maxDimension <= (isTouchDevice || isIPad ? TABLET_BREAKPOINT_MAX : 1024);

      // For desktop browsers without touch, use stricter limits
      if (!isTouchDevice && !isIPad) {
        const desktopTabletCheck = width >= TABLET_BREAKPOINT_MIN && width <= 1024;
        setIsTablet(desktopTabletCheck);
        return;
      }

      setIsTablet(isInTabletRange);
    };

    const tabletMediaQuery: MediaQueryList = window.matchMedia(
      `(min-width: ${TABLET_BREAKPOINT_MIN}px) and (max-width: ${IPAD_PRO_11_M4_WIDTH}px)`
    );

    tabletMediaQuery.addEventListener("change", checkTablet);
    window.addEventListener("orientationchange", checkTablet);
    window.addEventListener("resize", checkTablet);

    // Initial check
    checkTablet();

    return () => {
      tabletMediaQuery.removeEventListener("change", checkTablet);
      window.removeEventListener("orientationchange", checkTablet);
      window.removeEventListener("resize", checkTablet);
    };
  }, []);

  // Return undefined until hydrated to prevent SSR/client mismatch
  return isHydrated ? isTablet : undefined;
}
